import React, { useRef } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";

import { WSQueries } from "../../../query/WSQuery";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useCollaboratorQuery,
  usePayrollSettings
} from "../../../query/payments/queries";
import SidePanel from "../../../components/SidePanel";
import { Form1099Status } from "@wingspanhq/payments/dist/interfaces";
import { getCurrentYearForm1099Balances } from "../../../query/payments/selectors";
import RenderLoader from "../../../components/RenderLoader";

import Content from "./Content";
import ContentEditable from "./ContentEditable";

type Suffixes = { [key in Form1099Status]: string };

const TITLE_SUFFIXES: Suffixes = {
  [Form1099Status.Accepted]: "Details",
  [Form1099Status.Submitted]: "Details",
  [Form1099Status.NeedsActionInfo]: "Details",
  [Form1099Status.NeedsActionDispute]: "Details",
  [Form1099Status.Ready]: "Draft",
  [Form1099Status.Rejected]: "Details",
  [Form1099Status.Excluded]: "Draft"
};

type NEC1099FilingDetailsProps = RouteComponentProps<{
  collaboratorId: string;
}> & { onClose: () => void };

const NEC1099FilingDetails: React.FC<NEC1099FilingDetailsProps> = ({
  match,
  history,
  location,
  onClose
}) => {
  const pathRef = useRef(location.pathname);
  const userId = useUserId();
  const collaboratorQuery = useCollaboratorQuery(match.params.collaboratorId);
  const payrollSettingsQuery = usePayrollSettings(userId);

  return (
    <WSQueries
      queries={{ collaboratorQuery, payrollSettingsQuery }}
      renderLoader={RenderLoader}
    >
      {({
        collaboratorQuery: { data: collaborator },
        payrollSettingsQuery: { data: payrollSettings }
      }) => {
        const currentForm1099Balance = getCurrentYearForm1099Balances(
          collaborator
        );
        const form1099Status = currentForm1099Balance?.status;
        const titleSuffix =
          form1099Status && TITLE_SUFFIXES[form1099Status]
            ? ` ${TITLE_SUFFIXES[form1099Status]}`
            : "";
        const title = titleSuffix
          ? `1099-NEC${titleSuffix}`
          : `1099-NEC Details`;

        return (
          <Switch>
            <Route path={match.path} exact>
              <SidePanel title={title} onClose={onClose}>
                <Content
                  title={title}
                  collaborator={collaborator}
                  currentForm1099Balance={currentForm1099Balance}
                  payrollSettings={payrollSettings}
                />
              </SidePanel>
            </Route>
            <Route path={`${match.path}/edit`} exact>
              <SidePanel
                title={title}
                onClose={() => history.push(pathRef.current)}
              >
                <ContentEditable
                  collaborator={collaborator}
                  currentForm1099Balance={currentForm1099Balance}
                  payrollSettings={payrollSettings}
                  onCancel={() => history.push(pathRef.current)}
                />
              </SidePanel>
            </Route>
          </Switch>
        );
      }}
    </WSQueries>
  );
};

export default NEC1099FilingDetails;
